import React from 'react';
import { Zap, Clock, Activity, TrendingUp } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const CoreWebVitalsPanel = ({ vitals, detailed = false }) => {
  const getStatusColor = (status) => {
    if (status === 'good') return 'bg-green-100 text-green-800 border-green-300';
    if (status === 'needs-improvement') return 'bg-orange-100 text-orange-800 border-orange-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getStatusLabel = (status) => {
    if (status === 'good') return 'Bon';
    if (status === 'needs-improvement') return 'À améliorer';
    return 'Mauvais';
  };

  const metrics = [
    {
      key: 'lcp',
      label: 'LCP (Largest Contentful Paint)',
      description: 'Temps de chargement du contenu principal',
      icon: Clock,
      unit: 's',
      threshold: { good: 2.5, needsImprovement: 4.0 }
    },
    {
      key: 'fid',
      label: 'FID (First Input Delay)',
      description: 'Délai de première interaction',
      icon: Activity,
      unit: 'ms',
      threshold: { good: 100, needsImprovement: 300 }
    },
    {
      key: 'cls',
      label: 'CLS (Cumulative Layout Shift)',
      description: 'Stabilité visuelle de la page',
      icon: TrendingUp,
      unit: '',
      threshold: { good: 0.1, needsImprovement: 0.25 }
    },
    {
      key: 'ttfb',
      label: 'TTFB (Time to First Byte)',
      description: 'Temps de réponse du serveur',
      icon: Zap,
      unit: 's',
      threshold: { good: 0.8, needsImprovement: 1.8 }
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
        <Zap className="text-green-600" size={24} />
        Core Web Vitals
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics?.map((metric) => {
          const Icon = metric?.icon;
          const vitalData = vitals?.[metric?.key];
          const statusColor = getStatusColor(vitalData?.status);

          return (
            <div key={metric?.key} className="bg-gray-50 rounded-lg p-5 border border-gray-200">
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 bg-green-100 text-green-600 rounded-lg">
                  <Icon size={20} />
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColor}`}>
                  {getStatusLabel(vitalData?.status)}
                </span>
              </div>

              <p className="text-3xl font-bold text-gray-900 mb-1">
                {vitalData?.value}{metric?.unit}
              </p>
              <p className="text-sm font-medium text-gray-900 mb-1">{metric?.label}</p>
              
              {detailed && (
                <>
                  <p className="text-xs text-gray-600 mb-3">{metric?.description}</p>
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Seuils recommandés:</p>
                    <p className="text-xs text-green-600">Bon: &lt; {metric?.threshold?.good}{metric?.unit}</p>
                    <p className="text-xs text-orange-600">Moyen: &lt; {metric?.threshold?.needsImprovement}{metric?.unit}</p>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {detailed && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-900">
            <strong>Note:</strong> Les Core Web Vitals sont des métriques essentielles pour le référencement Google. 
            Un bon score améliore le classement dans les résultats de recherche et l'expérience utilisateur.
          </p>
        </div>
      )}
    </div>
  );
};

export default CoreWebVitalsPanel;